import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { chiusureApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Sun, 
  Moon,
  Users,
  Banknote,
  CreditCard,
  Building2,
  FileText,
  Ticket,
  StickyNote,
  Loader2,
  CalendarDays,
  Receipt,
  Cloud,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';

const ChiusuraDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [chiusura, setChiusura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadChiusura();
  }, [id]);

  const loadChiusura = async () => {
    try {
      const data = await chiusureApi.getById(id);
      setChiusura(data);
    } catch (error) {
      toast.error('Chiusura non trovata');
      navigate('/chiusure');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await chiusureApi.delete(id);
      toast.success('Chiusura eliminata');
      navigate('/chiusure');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const getDayBadgeClass = (giorno) => {
    const classes = {
      'LUN': 'day-badge-lun',
      'MAR': 'day-badge-mar',
      'MER': 'day-badge-mer',
      'GIO': 'day-badge-gio',
      'VEN': 'day-badge-ven',
      'SAB': 'day-badge-sab',
      'DOM': 'day-badge-dom',
    };
    return classes[giorno] || 'bg-muted text-muted-foreground';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const DataRow = ({ icon: Icon, label, value, isCurrency = false, highlight = false }) => (
    <div className={cn(
      "flex items-center justify-between py-3 border-b border-border last:border-0",
      highlight && "bg-accent/50 -mx-4 px-4 rounded-lg"
    )}>
      <div className="flex items-center gap-3 text-muted-foreground">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <span className={cn(
        "font-semibold",
        isCurrency && "font-mono-numbers",
        highlight && "text-primary text-lg"
      )}>
        {isCurrency ? formatCurrency(value) : value}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!chiusura) return null;

  const hasDelta = chiusura.delta_cassa_cloud_vs_incasso !== 0;
  const hasFiscalData = chiusura.totale_cassa_in_cloud > 0 || chiusura.totale_scontrini > 0 || chiusura.totale_fatture > 0;

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/chiusure')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold font-heading tracking-tight">Dettaglio</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => navigate(`/chiusure/${id}/modifica`)}
              data-testid="edit-btn"
            >
              <Edit className="w-4 h-4" />
            </Button>
            {isAdmin() && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    data-testid="delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Elimina chiusura</AlertDialogTitle>
                    <AlertDialogDescription>
                      Sei sicuro di voler eliminare questa chiusura? Questa azione non può essere annullata.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={deleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Elimina'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-4">
        {/* Date Card */}
        <Card className="bg-primary text-primary-foreground">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CalendarDays className="w-10 h-10 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">Data Servizio</p>
                  <p className="text-2xl font-bold font-heading">
                    {format(new Date(chiusura.data_servizio), 'd MMMM yyyy', { locale: it })}
                  </p>
                </div>
              </div>
              <span className={`day-badge ${getDayBadgeClass(chiusura.giorno_settimana)} text-lg px-4 py-2`}>
                {chiusura.giorno_settimana}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* RIEPILOGO GIORNO Card */}
        <Card className="border-2 border-primary/20 bg-accent/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" />
              Riepilogo Giorno
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Coperti Totali</p>
                <p className="text-3xl font-bold font-mono-numbers text-primary" data-testid="coperti-totali">
                  {chiusura.coperti_totali_giorno}
                </p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Incasso Giorno</p>
                <p className="text-2xl font-bold font-mono-numbers text-green-600" data-testid="incasso-giorno">
                  {formatCurrency(chiusura.incasso_giorno)}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground pt-2">
              <span>Pranzo: {chiusura.coperti_pranzo} coperti</span>
              <span>Cena: {chiusura.coperti_cena} coperti</span>
            </div>
          </CardContent>
        </Card>

        {/* CONTROLLO FISCALE Card */}
        {hasFiscalData && (
          <Card className={cn(
            "border-2",
            hasDelta 
              ? "border-amber-300 bg-amber-50/50" 
              : "border-green-300 bg-green-50/50"
          )}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Cloud className="w-5 h-5" />
                Controllo Cassa Cloud
                {hasDelta ? (
                  <span className="ml-auto flex items-center gap-1 text-sm font-normal text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    Differenza
                  </span>
                ) : (
                  <span className="ml-auto flex items-center gap-1 text-sm font-normal text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    OK
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Input Values */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-white rounded-lg">
                  <p className="text-xs text-muted-foreground">Scontrini</p>
                  <p className="font-mono-numbers font-semibold" data-testid="totale-scontrini">
                    {formatCurrency(chiusura.totale_scontrini)}
                  </p>
                </div>
                <div className="p-2 bg-white rounded-lg">
                  <p className="text-xs text-muted-foreground">Fatture</p>
                  <p className="font-mono-numbers font-semibold" data-testid="totale-fatture">
                    {formatCurrency(chiusura.totale_fatture)}
                  </p>
                </div>
                <div className="p-2 bg-white rounded-lg">
                  <p className="text-xs text-muted-foreground">Cassa Cloud</p>
                  <p className="font-mono-numbers font-semibold" data-testid="totale-cassa-cloud">
                    {formatCurrency(chiusura.totale_cassa_in_cloud)}
                  </p>
                </div>
              </div>

              {/* Calculated Values */}
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div>
                    <p className="text-sm font-medium">Proforma</p>
                    <p className="text-xs text-muted-foreground">Cloud - Scontrini - Fatture</p>
                  </div>
                  <span className="font-mono-numbers font-bold text-lg" data-testid="proforma">
                    {formatCurrency(chiusura.proforma)}
                  </span>
                </div>
                
                <div className={cn(
                  "flex items-center justify-between p-3 rounded-lg",
                  hasDelta 
                    ? "bg-amber-100 border-2 border-amber-300" 
                    : "bg-green-100 border-2 border-green-300"
                )}>
                  <div>
                    <p className="text-sm font-medium">Delta Cloud vs Incasso</p>
                    <p className="text-xs text-muted-foreground">Cloud - Incasso Giorno</p>
                  </div>
                  <span className={cn(
                    "font-mono-numbers font-bold text-lg",
                    hasDelta ? "text-amber-700" : "text-green-700"
                  )} data-testid="delta-cassa-cloud">
                    {chiusura.delta_cassa_cloud_vs_incasso > 0 ? '+' : ''}
                    {formatCurrency(chiusura.delta_cassa_cloud_vs_incasso)}
                  </span>
                </div>

                {hasDelta && (
                  <div className="flex items-center gap-2 p-3 bg-amber-100 rounded-lg text-amber-800 text-sm">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <p>
                      {chiusura.delta_cassa_cloud_vs_incasso > 0 
                        ? `La Cassa Cloud registra ${formatCurrency(Math.abs(chiusura.delta_cassa_cloud_vs_incasso))} in più rispetto all'incasso effettivo.`
                        : `La Cassa Cloud registra ${formatCurrency(Math.abs(chiusura.delta_cassa_cloud_vs_incasso))} in meno rispetto all'incasso effettivo.`
                      }
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* PRANZO Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-500" />
              Pranzo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {/* Totali Pranzo */}
            <div className="bg-amber-50 rounded-xl p-4 mb-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Coperti</p>
                  <p className="text-xl font-bold font-mono-numbers" data-testid="coperti-pranzo">{chiusura.coperti_pranzo}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Incasso</p>
                  <p className="text-xl font-bold font-mono-numbers text-green-600" data-testid="incasso-pranzo">{formatCurrency(chiusura.incasso_totale_pranzo)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Spesa Media</p>
                  <p className="text-xl font-bold font-mono-numbers text-primary" data-testid="spesa-media-pranzo">
                    {chiusura.spesa_media_pranzo > 0 ? formatCurrency(chiusura.spesa_media_pranzo) : '—'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Dettaglio incassi */}
            <DataRow icon={Banknote} label="Contanti" value={chiusura.contanti_pranzo} isCurrency />
            <DataRow icon={CreditCard} label="POS" value={chiusura.pos_pranzo} isCurrency />
            <DataRow icon={Building2} label="Bonifico" value={chiusura.bonifico_pranzo} isCurrency />
            <DataRow icon={FileText} label="Assegno" value={chiusura.assegno_pranzo} isCurrency />
            <DataRow icon={Ticket} label="Buoni pasto" value={chiusura.buoni_pasto_pranzo} isCurrency />
            
            {chiusura.note_pranzo && (
              <div className="mt-4 p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <StickyNote className="w-4 h-4" />
                  Note
                </div>
                <p className="text-sm">{chiusura.note_pranzo}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CENA Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Moon className="w-5 h-5 text-indigo-500" />
              Cena
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {/* Totali Cena */}
            <div className="bg-indigo-50 rounded-xl p-4 mb-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Coperti</p>
                  <p className="text-xl font-bold font-mono-numbers" data-testid="coperti-cena">{chiusura.coperti_cena}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Incasso</p>
                  <p className="text-xl font-bold font-mono-numbers text-green-600" data-testid="incasso-cena">{formatCurrency(chiusura.incasso_totale_cena)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Spesa Media</p>
                  <p className="text-xl font-bold font-mono-numbers text-primary" data-testid="spesa-media-cena">
                    {chiusura.spesa_media_cena > 0 ? formatCurrency(chiusura.spesa_media_cena) : '—'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Dettaglio incassi */}
            <DataRow icon={Banknote} label="Contanti" value={chiusura.contanti_cena} isCurrency />
            <DataRow icon={CreditCard} label="POS" value={chiusura.pos_cena} isCurrency />
            <DataRow icon={Building2} label="Bonifico" value={chiusura.bonifico_cena} isCurrency />
            <DataRow icon={FileText} label="Assegno" value={chiusura.assegno_cena} isCurrency />
            <DataRow icon={Ticket} label="Buoni pasto" value={chiusura.buoni_pasto_cena} isCurrency />
            
            {chiusura.note_cena && (
              <div className="mt-4 p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <StickyNote className="w-4 h-4" />
                  Note
                </div>
                <p className="text-sm">{chiusura.note_cena}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metadata */}
        <p className="text-xs text-center text-muted-foreground">
          Inserita il {format(new Date(chiusura.data_inserimento), "d MMM yyyy 'alle' HH:mm", { locale: it })}
        </p>
      </main>
    </div>
  );
};

export default ChiusuraDetailPage;
